import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { NotAvailableState } from "@/components/ui/system-state";

/**
 * Provider Health tile — design port (ALL-5825, dashboard lane task 5).
 *
 * The prototype tile takes `providers: ProviderHealth[]` and
 * `chains: ChainHealth[]` (from the `PROVIDERS` / `CHAINS` fixtures in
 * `features/oversight/oversight-data`) and reads `FRESHNESS_ROWS` directly. It
 * renders a fleet summary (providers healthy, average uptime, probes OK, worst
 * p99), a per-provider 10-probe latency timeline with p50 and uptime, a network
 * chain row and a source-freshness matrix.
 *
 * None of that data has a source in this app: there is no `/v1/monitoring/*` or
 * `/v1/chain/*` gateway route (`src/lib/api.ts` has none), and the
 * `/monitoring/provider`, `/monitoring/chain` and `/monitoring` routes are
 * `DeferredPage`s. `/monitoring/freshness` exists but is display-only — it takes
 * its `FreshnessState` as a prop and is not wired to an endpoint either.
 *
 * An uptime percentage, a latency bar and a "stale"/"fresh" badge are
 * observations; rendering them from a fixture would put invented operational
 * telemetry on the home dashboard (ALL-4747 G6), so this tile keeps the
 * designed header and renders the designed not-available state. Nothing here is
 * estimated or inferred.
 *
 * The header link is kept as designed: it points at the deferred `/monitoring`
 * overview, and the not-available body guards the tile from reading as a live
 * fleet. The prototype's subtitle ("Live probes every 5 min · …") is dropped:
 * no probe runs on any schedule here, so the schedule claim was itself an
 * invented operational fact. The neutral subtitle that remains names only what
 * the tile is about.
 */
export function ProviderChainHealth() {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Provider Health</h2>
          <div className="mt-0.5 text-xs text-muted">
            Custody, stablecoin and tokenization providers
          </div>
        </div>
        <Link
          to="/monitoring"
          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          View all <ArrowUpRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      <NotAvailableState
        className="flex-1 justify-center"
        title="Provider health is not available yet"
        description="Provider probes, uptime, latency, chain status and source freshness are not collected in this environment, so no fleet summary, probe timeline or freshness matrix is shown here."
        reason="No gateway route serves provider or chain health — there is no /v1/monitoring/* or /v1/chain/* route in this app."
      />
    </div>
  );
}
